// React and core hooks
import React, { PureComponent, useEffect, useState, useRef } from "react";

// Ant Design components
import { 
  theme, 
  Row, 
  Button, 
  Spin, 
  Skeleton, 
  Modal, 
  Tooltip, 
  Tag, 
  Select 
} from "antd";
import {
  InfoCircleOutlined,
  UndoOutlined,
  GroupOutlined,
  FilterOutlined,
  TableOutlined,
  CalendarOutlined,
  PlusOutlined,
} from "@ant-design/icons";

// Third-party libraries
import Papa from "papaparse";

// Types
import { SearchState, Condition } from "../types";
import { SnippetV1State, DeepLinkType, BoardCardData, ExploreCard } from "pages/api/deep-links/types";
import type { Message, ResultSummaryResponse, FollowUpPrompt } from "./Chat";
import { Duration } from "src/utils/date";

// Local components
import { Chat } from "./Chat";
import { QuestionInput } from "./inputs/QuestionInput";
import { DateRangeSelect } from "src/components/DateRangeSelect";
import { FilterConditionPanel } from "./inputs/question-input/FilterConditionPanel";
import { DataSourcePicker } from "src/components/new-card-picker/DataSourcePicker";

// Hooks
import { useApplicationContext } from "src/hooks/useApplication";
import { usePopupContext } from "src/hooks/usePopup";
import { useTeams } from "src/hooks/useTeams";

// API and utilities
import {
  getEntities,
  groupEvents,
  getResultSummariesBulk,
} from "pages/api-clients/graph-search";
import { DefaultPrefixKeys } from "pages/api-clients/narrative-prefix";
import { timeframeToString } from "src/utils/date";
import { formatTitle } from "src/utils/strings";
import { conditionKey } from "src/utils/fields";

// Type definitions
export interface ChatSession {
  title: string;
  initialPrompt: string;
  timeframe: Pick<SearchState, "duration" | "startDate" | "endDate">;
  dataSources: Array<{
    query: SearchState;
    format: "csv";
  }>;
}

interface TimeframeData {
  duration?: Duration;
  startDate?: string;
  endDate?: string;
}

interface DataSource {
  query: SearchState;
  format: "csv";
}

/**
 * Props for the ChatAssistant component
 */
interface ChatAssistantProps {
  /** Optional title for the chat assistant */
  title?: string;
  /** Extra content to display next to the title */
  titleExtra?: React.ReactNode;
  /** Initial prompt to start the chat */
  initialPrompt: string;
  /** Data sources for the chat to analyze */
  dataSources: DataSource[];
  /** Time range for data analysis */
  timeframe: Pick<SearchState, "duration" | "startDate" | "endDate">;
  /** Callback when the assistant is closed */
  onClose(): void;
  /** Whether to render inline or as a separate panel */
  inline?: boolean;
  /** Whether to show the data sources view */
  showQueries?: boolean;
  /** Callback to toggle data sources view */
  setShowQueries?: (show: boolean) => void;
  /** Additional context data */
  context?: any;
  /** Global filters to apply */
  globalFilters?: Condition[];
  /** Callback when timeframe changes */
  onTimeframeChange?: (timeframe: TimeframeData) => void;
  /** Callback when filters change */
  onFiltersChange?: (filters: Condition[]) => void;
  /** Callback when team selection changes */
  onTeamChange?: (team: string | undefined) => void;
}

export function ChatAssistant(props: ChatAssistantProps) {
  const {
    title,
    titleExtra,
    initialPrompt,
    dataSources,
    timeframe,
    inline = false,
    showQueries,
    setShowQueries,
    context,
    globalFilters = [],
    onTimeframeChange,
    onFiltersChange,
    onTeamChange,
  } = props;
  
  // Local state for timeframe, filters, and team (independent of parent)
  const [localTimeframe, setLocalTimeframe] = React.useState(timeframe);
  const [localFilters, setLocalFilters] = React.useState(globalFilters);
  const [localTeam, setLocalTeam] = React.useState<string | undefined>(undefined);
  
  // Local state for managing data sources
  const [localDataSources, setLocalDataSources] = React.useState(dataSources);
  const [dataSourcesData, setDataSourcesData] = React.useState<Record<string, BoardCardData>>({});
  
  // Initialize local state when props change (but don't update parent)
  React.useEffect(() => {
    setLocalTimeframe(timeframe);
  }, [timeframe.duration, timeframe.startDate, timeframe.endDate]);
  
  React.useEffect(() => {
    setLocalFilters(globalFilters);
  }, [JSON.stringify(globalFilters)]);
  
  React.useEffect(() => {
    setLocalDataSources(dataSources);
  }, [JSON.stringify(dataSources)]);
  
  const { token } = theme.useToken();
  const orgId = useApplicationContext().org_id!;
  
  // Use utility functions for data transformation
  const convertExploreCardToSearchState = React.useCallback(
    (card: ExploreCard): SearchState => convertExploreCardData(card, orgId),
    [orgId]
  );
  
  // Get active data sources (original + added ones)
  const activeDataSources = React.useMemo(() => {
    const additionalSources = Object.entries(dataSourcesData)
      .filter(([_, card]) => card.type === DeepLinkType.EXPLORE)
      .map(([_, card]) => ({
        query: convertExploreCardToSearchState(card as ExploreCard),
        format: "csv" as const,
      }));
    
    return [...localDataSources, ...additionalSources];
  }, [localDataSources, dataSourcesData, orgId]);
  
  
  // Convert team selection to filter condition
  const teamFilter = React.useMemo(() => {
    if (!localTeam) return [];
    return createTeamFilter(localTeam);
  }, [localTeam]);
  
  // Combine all local filters (user filters + team filter)
  const allLocalFilters = React.useMemo(() => {
    return mergeFiltersWithTeam(localFilters, teamFilter);
  }, [JSON.stringify(localFilters), JSON.stringify(teamFilter)]);
  
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);

  // Extract query details for pills
  const query = activeDataSources[0]?.query;
  const queryFilterCount = query?.filters?.length || 0;
  const localFilterCount = allLocalFilters?.length || 0;
  const totalFilterCount = queryFilterCount + localFilterCount;
  const entityTypes = query?.select || [];

  const [localShowQueries, setLocalShowQueries] = useState<boolean>(false);
  const [snippet, setSnippet] = useState<SearchState>({ orgId } as SearchState);
  
  // Refs for triggering UI elements
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const datePillRef = useRef<HTMLElement>(null);
  const teamPillRef = useRef<HTMLElement>(null);
  const filterPillRef = useRef<HTMLElement>(null);

  // Use either controlled or local state for showQueries
  const isShowingQueries = showQueries ?? localShowQueries;
  const toggleQueries = setShowQueries ?? setLocalShowQueries;

  // Use the custom hook for pill handlers
  const { handleFilterPillClick, handleDatePillClick, handleTeamPillClick } = usePillHandlers({
    localFilters,
    localTimeframe,
    localTeam,
    setLocalTimeframe,
    setLocalTeam,
    onFiltersChange,
    onTimeframeChange,
    onTeamChange,
    filterPillRef,
    filterBtnRef,
    datePillRef,
    teamPillRef,
  });

  const handleSend = async (
    prompt: string,
    messages: Message[],
    setMessages: (messages: Message[]) => void
  ): Promise<void> => {
    if (loading || !prompt.trim()) return;

    setLoading(true);

    try {
      if (!orgId) {
        throw new Error("Organization ID is not available");
      }

      // Transform queries for the API - merge local filters with each query
      const queries = activeDataSources.map((source) => {
        // Filter out any existing Activity.Team filters from source query to prevent duplicates
        const sourceFiltersWithoutTeam = (source.query.filters || []).filter(
          filter => filter.key !== "Activity.Team"
        );
        
        return {
          ...source.query,
          orgId,
          // Apply local timeframe and filters
          duration: localTimeframe.duration || source.query.duration,
          startDate: localTimeframe.startDate || source.query.startDate,
          endDate: localTimeframe.endDate || source.query.endDate,
          filters: [...allLocalFilters, ...sourceFiltersWithoutTeam],
        };
      });

      // Debug: Log the final request payload
      const requestPayload = {
        sections: [
          {
            prompt,
            queries,
          },
        ],
      };

      // Send message to the AI and get the response
      const response = (await getResultSummariesBulk(
        requestPayload,
        orgId,
        DefaultPrefixKeys.AGENT,
        undefined, // promptFormat
        conversationId
      )) as ResultSummaryResponse;

      // Validate response structure
      if (!response) {
        throw new Error("Empty response from server");
      }

      // Store conversationId from response if present
      if (response.conversationId && !conversationId) {
        setConversationId(response.conversationId);
      }

      const botMessage: Message = {
        text: response.summary,
        sender: "bot",
        prompts:
          response.follow_up_prompts?.map((p: FollowUpPrompt) => ({
            title: p.title,
            prompt: p.prompt,
          })) || [],
        copyEnabled: true,
        isStreaming: true,
      };
      setMessages([...messages, botMessage]);
    } catch (error_) {
      let errorMessage = "Failed to get result summaries";

      if (error_ instanceof Error) {
        if (error_.message.includes("rate limit")) {
          errorMessage = "Rate limit exceeded. Please try again in a moment.";
        } else if (error_.message.includes("Empty response")) {
          errorMessage = "Server returned an empty response. Please try again.";
        } else {
          errorMessage = error_.message;
        }
      }

      const errorBotMessage: Message = {
        text: errorMessage,
        sender: "bot",
        prompts: [],
        copyEnabled: false,
        isStreaming: false,
      };
      setMessages([...messages, errorBotMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Initialize when dataSources change
  useEffect(() => {
    setSnippet({ orgId } as SearchState);

    // Maintain current view state
    if (setShowQueries === undefined) {
      setLocalShowQueries(isShowingQueries);
    }
  }, [activeDataSources, orgId, setShowQueries, isShowingQueries]);

  const content = (
    <>
      <Row gutter={10} style={{ height: "100%" }}>
        <div style={{ width: "100%", position: "relative", height: "100%" }}>
          <div
            style={{
              display: isShowingQueries ? "none" : "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <div style={{ flex: 1, minHeight: 0 }}>
              <Chat
                title={title || "Analysis"}
                dataSources={activeDataSources}
                snippet={snippet}
                defaultPrompt={initialPrompt}
                handleSend={handleSend}
                loading={loading}
                inline={inline}
                contextLabel={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "6px",
                        alignItems: "center",
                      }}
                    >
                      <DataSourcesPill 
                        count={activeDataSources.length} 
                        onClick={() => toggleQueries(true)} 
                      />
                      
                      <FiltersPill 
                        ref={filterPillRef}
                        count={totalFilterCount} 
                        onClick={handleFilterPillClick} 
                      />
                      
                      {onTimeframeChange && (
                        <DateRangePill 
                          ref={datePillRef}
                          timeframe={localTimeframe} 
                          onClick={handleDatePillClick} 
                        />
                      )}
                      
                      {onTeamChange && (
                        <TeamPill 
                          ref={teamPillRef}
                          team={localTeam} 
                          onClick={handleTeamPillClick} 
                        />
                      )}
                    </div>
                    <div style={{ display: 'none' }}>
                      {/* Hidden filter button for triggering - used by filter pill */}
                      <button 
                        ref={filterBtnRef} 
                        style={{ display: 'none' }} 
                        data-testid="chat-filter-btn" 
                      />
                    </div>
                  </div>
                }
              />
            </div>
          </div>
          <div
            style={{
              display: isShowingQueries === true ? "block" : "none",
              height: "100%",
              background: token.colorBgElevated,
              padding: "16px 24px",
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  color: token.colorText,
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Data Sources ({activeDataSources.length} source
                {activeDataSources.length === 1 ? "" : "s"})
              </div>
              <Button
                type="link"
                onClick={() => toggleQueries(false)}
                size="small"
                style={{ padding: 0 }}
              >
                Back to Chat
              </Button>
            </div>
            
            {/* Data Source Picker */}
            <div style={{ marginBottom: 16, padding: "0 4px" }}>
              <DataSourcePicker
                existingData={dataSourcesData}
                onUpdate={setDataSourcesData}
                onRemove={(cardId) => {
                  const newData = { ...dataSourcesData };
                          delete newData[cardId];
                          setDataSourcesData(newData);
                        }}
                        onSelect={() => {
                          // Data source selected
                        }}
                        renderTrigger={(onClick) => (
                          <Button
                            type="dashed"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={onClick}
                            style={{ width: "100%" }}
                          >
                            Add Data Source
                          </Button>
                        )}
                      />
                    </div>
            
            {activeDataSources.map((source, index) => (
              <div
                key={index}
                style={{
                  marginBottom: index < activeDataSources.length - 1 ? 16 : 0,
                  width: "100%",
                }}
              >
                <div
                  style={{
                    backgroundColor: token.colorBgContainer,
                    padding: "16px",
                    borderRadius: "8px",
                    width: "100%",
                    border: `1px solid ${token.colorBorderSecondary}`,
                  }}
                >
                  <div style={{ marginBottom: 12 }}>
                    <Tooltip title="Results are limited to the first 5 rows">
                      <InfoCircleOutlined
                        style={{
                          fontSize: 14,
                          color: token.colorTextSecondary,
                          marginRight: 8,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 12,
                          color: token.colorTextSecondary,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                        }}
                      >
                        Source {index + 1}
                      </span>
                    </Tooltip>
                  </div>
                  <QuestionDataPreview 
                    searchState={{
                      ...source.query,
                      // Apply local timeframe and filters to preview
                      duration: localTimeframe.duration || source.query.duration,
                      startDate: localTimeframe.startDate || source.query.startDate,
                      endDate: localTimeframe.endDate || source.query.endDate,
                      filters: [...allLocalFilters, ...(source.query.filters || []).filter(
                        filter => filter.key !== "Activity.Team"
                      )],
                    }} 
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Row>
    </>
  );

  return inline ? (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: token.colorBgElevated,
      }}
    >
      <div style={{ flex: 1, overflow: "auto", padding: "24px 32px" }}>
        {content}
      </div>
    </div>
  ) : (
    <div
      style={{
        position: "relative",
        height: "100%",
        width: "100%",
        backgroundColor: token.colorBgElevated,
        borderLeft: `1px solid ${token.colorBorderSecondary}`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          height: 50,
          width: "100%",
          padding: "0 24px",
          background: token.colorBgElevated,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: `${token.boxShadowTertiary}`,
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ fontSize: 16, fontWeight: 500 }}>{title}</div>
            {titleExtra}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <DataSourcesPill 
              count={activeDataSources.length} 
              onClick={() => toggleQueries(true)} 
            />
            
            <EntitiesPill 
              entities={entityTypes} 
              onClick={() => toggleQueries(true)} 
            />
            
            <FiltersPill 
              ref={filterPillRef}
              count={totalFilterCount} 
              onClick={handleFilterPillClick} 
            />
            
            {onTimeframeChange && (
              <DateRangePill 
                ref={datePillRef}
                timeframe={localTimeframe} 
                onClick={handleDatePillClick} 
              />
            )}
            
            {onTeamChange && (
              <TeamPill 
                ref={teamPillRef}
                team={localTeam} 
                onClick={handleTeamPillClick} 
              />
            )}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "24px 32px" }}>
        {content}
      </div>
    </div>
  );
}
const QuestionDataPreview = ({ searchState }: { searchState: SearchState }) => {
  const orgId = useApplicationContext().org_id!;
  const [data, setData] = useState<Record<string, any>[] | undefined>(
    undefined
  );
  const [headers, setHeaders] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load data when component mounts or searchState changes
  useEffect(() => {
    if (!orgId) {
      return;
    }

    setLoading(true);
    setError(null);

    const fetchData = async () => {
      try {
        const response = searchState.groupBys?.length
          ? await groupEvents({ ...searchState, format: "csv" }, orgId!, {
              limit: 5,
            })
          : await getEntities({ ...searchState, format: "csv" }, 1, 5, orgId!);

        const parsedData = Papa.parse(response.data as unknown as string, {
          header: true,
        });

        const validData = parsedData.data as Record<string, any>[];
        setData(validData);
        setHeaders(validData.length > 0 ? Object.keys(validData[0]) : []);
      } catch (error_) {
        console.error("Error fetching data:", error_);
        let errorMessage = "Failed to fetch data";
        if (error_ instanceof Error) {
          errorMessage = error_.message;
          // Add more specific error handling for known error types
          if (error_.message.includes("404")) {
            errorMessage = "Data source not found";
          } else if (error_.message.includes("401")) {
            errorMessage = "Not authorized to access this data";
          }
        }
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [orgId, JSON.stringify(searchState)]); // Use stringified version to catch all changes

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      {loading && <Skeleton active />}
      {error && (
        <div style={{ color: "red", marginBottom: "8px" }}>Error: {error}</div>
      )}
      {!loading && !error && data && data.length === 0 && (
        <>No data returned. Please adjust your query to get results.</>
      )}
      {!loading && !error && data && data.length > 0 && (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th
                  key={index}
                  style={{
                    padding: "8px",
                    textAlign: "left",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {headers.map((header, colIndex) => (
                  <td
                    key={colIndex}
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid rgba(255, 255, 255, 0.06)",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row[header]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};


export class PureQueryInput extends PureComponent<{
  serialized: string;
  onChange(searchState: SearchState): void;
  onReset(): void;
}> {
  lastRun: { when: number; serial: string };
  constructor(props: {
    serialized: string;
    onChange(searchState: SearchState): void;
    onReset(): void;
  }) {
    /* console.log("constructing"); */
    super(props);
    this.lastRun = {
      when: Date.now(),
      serial: props.serialized,
    };
  }

  render() {
    this.lastRun = { when: Date.now(), serial: this.props.serialized };
    const value = JSON.parse(this.props.serialized) as SearchState;
    return (
      <QuestionInput
        alternateStyle
        seed={this.props.serialized}
        value={value}
        resetTitle="Reset to defaults"
        resetIcon={<UndoOutlined style={{ fontSize: 14 }} />}
        hideDisabled
        onChange={this.props.onChange}
        handleReset={this.props.onReset}
        options={{
          disableGroupBys: true,
          disableMeasures: true,
          disableSelect: true,
          disableSearchTerm: true,
          disableCompareWith: true,
          disableFormula: true,
          disableHaving: true,
          disableSnippets: false,
          disableSuggestions: false,
          addFromTeamSnippet: false,
        }}
        snippetTransformer={(state) =>
          ({
            duration: state.duration,
            startDate: state.startDate,
            endDate: state.endDate,
            filters: state.filters,
          } as SearchState)
        }
        snippetOptionFilter={(snippet) => {
          const json = snippet?.json as SnippetV1State | undefined;
          return (json?.params?.filters?.length || 0) > 0;
        }}
      />
    );
  }
}

// Reusable Interactive Pill Component
const InteractivePill = React.forwardRef<HTMLElement, {
  icon?: React.ReactNode;
  children: React.ReactNode;
  onClick?: () => void;
  color?: string;
  loading?: boolean;
  style?: React.CSSProperties;
}>(({ icon, children, onClick, color, loading = false, style = {} }, ref) => {
  const { token } = theme.useToken();
  
  const pillStyle: React.CSSProperties = {
    margin: 0,
    fontSize: "10px",
    cursor: onClick ? "pointer" : "default",
    transition: "all 0.2s ease",
    userSelect: "none",
    ...style,
  };

  if (onClick) {
    pillStyle.backgroundColor = token.colorFillTertiary;
    pillStyle.border = "none";
    pillStyle.color = token.colorText;
  }

  return (
    <Tag
      ref={ref}
      icon={loading ? <Spin size="small" /> : icon}
      color={color}
      style={pillStyle}
      onClick={onClick}
    >
      {children}
    </Tag>
  );
});

InteractivePill.displayName = 'InteractivePill';

// Individual Pill Components
const DataSourcesPill = ({ count, onClick }: { count: number; onClick: () => void }) => (
  <InteractivePill icon={<TableOutlined />} onClick={onClick}>
    {count} Data Source{count === 1 ? "" : "s"}
  </InteractivePill>
);

const EntitiesPill = ({ entities, onClick }: { entities: string[]; onClick: () => void }) => {
  if (entities.length === 0) return null;
  
  return (
    <InteractivePill onClick={onClick}>
      Analyzing: {entities.map(formatTitle).join(", ")}
    </InteractivePill>
  );
};

const FiltersPill = React.forwardRef<HTMLElement, { count: number; onClick: () => void }>(({ count, onClick }, ref) => {
  if (count === 0) return null;
  
  return (
    <InteractivePill ref={ref} icon={<FilterOutlined />} color="orange" onClick={onClick}>
      {count} Filter{count > 1 ? "s" : ""}
    </InteractivePill>
  );
});

FiltersPill.displayName = 'FiltersPill';

const DateRangePill = React.forwardRef<HTMLElement, { 
  timeframe: Pick<SearchState, "duration" | "startDate" | "endDate">; 
  onClick: () => void;
}>(({ timeframe, onClick }, ref) => (
  <InteractivePill ref={ref} icon={<CalendarOutlined />} onClick={onClick}>
    {timeframeToString(timeframe)}
  </InteractivePill>
));

DateRangePill.displayName = 'DateRangePill';

const TeamPill = React.forwardRef<HTMLElement, { 
  team?: string; 
  onClick: () => void;
}>(({ team, onClick }, ref) => {
  return (
    <InteractivePill ref={ref} icon={<GroupOutlined />} color="blue" onClick={onClick}>
      {team ? `Team: ${team}` : 'All Teams'}
    </InteractivePill>
  );
});

TeamPill.displayName = 'TeamPill';

// Custom Hooks
interface PillHandlersParams {
  localFilters: Condition[];
  localTimeframe: Pick<SearchState, "duration" | "startDate" | "endDate">;
  localTeam?: string;
  setLocalTimeframe: (timeframe: Pick<SearchState, "duration" | "startDate" | "endDate">) => void;
  setLocalTeam: (team: string | undefined) => void;
  onFiltersChange?: (filters: Condition[]) => void;
  onTimeframeChange?: (timeframe: TimeframeData) => void;
  onTeamChange?: (team: string | undefined) => void;
  filterPillRef: React.RefObject<HTMLElement>;
  filterBtnRef: React.RefObject<HTMLButtonElement>;
  datePillRef: React.RefObject<HTMLElement>;
  teamPillRef: React.RefObject<HTMLElement>;
}

/**
 * Custom hook for handling pill interactions
 */
function usePillHandlers(params: PillHandlersParams) {
  const popup = usePopupContext();
  const teams = useTeams();

  const handleFilterPillClick = React.useCallback(() => {
    if (!params.onFiltersChange) return;
    
    const anchor = params.filterPillRef.current || params.filterBtnRef.current;
    if (!anchor) return;
    
    popup.setPopup(
      anchor,
      <FilterConditionPanel
        initialValue={{}}
        value={{ filters: params.localFilters }}
        onApply={(data) => {
          if ("filters" in data) {
            params.onFiltersChange!(data.filters as Condition[]);
          } else if (data.field && data.operator && data.values?.length) {
            const newCondition: Condition = {
              ...data.field,
              comparison: { [data.operator]: data.values } as any,
              key: conditionKey({
                ...data.field,
                comparison: { [data.operator]: data.values } as any,
                key: "",
              } as Condition),
            };
            const merged: Condition[] = [
              ...(params.localFilters || []).filter(
                (f: Condition) => f.key !== newCondition.key
              ),
              newCondition,
            ];
            params.onFiltersChange!(merged);
          }
          popup.close();
        }}
        onCancel={() => popup.close()}
        showAppliedSummary
      />
    );
  }, [params.onFiltersChange, params.localFilters, params.filterPillRef, params.filterBtnRef, popup]);

  const handleDatePillClick = React.useCallback(() => {
    if (!params.onTimeframeChange) return;
    
    const anchor = params.datePillRef.current;
    if (!anchor) return;
    
    popup.setPopup(
      anchor,
      <div style={{ padding: 8, minWidth: 300 }}>
        <DateRangeSelect
          duration={
            params.localTimeframe.duration &&
            String(params.localTimeframe.duration) !== ""
              ? params.localTimeframe.duration
              : undefined
          }
          startDate={params.localTimeframe.startDate}
          endDate={params.localTimeframe.endDate}
          onChangeDuration={(val) => {
            const newTimeframe = {
              duration: val,
              startDate: undefined,
              endDate: undefined,
            };
            params.setLocalTimeframe(newTimeframe);
            if (params.onTimeframeChange) {
              params.onTimeframeChange(newTimeframe);
            }
            popup.close();
          }}
          onChangeRange={([start, end]) => {
            const newTimeframe = {
              duration: undefined,
              startDate: start,
              endDate: end,
            };
            params.setLocalTimeframe(newTimeframe);
            if (params.onTimeframeChange) {
              params.onTimeframeChange(newTimeframe);
            }
            popup.close();
          }}
        />
      </div>
    );
  }, [params.onTimeframeChange, params.localTimeframe, params.datePillRef, params.setLocalTimeframe, popup]);

  const handleTeamPillClick = React.useCallback(() => {
    if (!params.onTeamChange) return;
    
    const anchor = params.teamPillRef.current;
    if (!anchor) return;
    
    popup.setPopup(
      anchor,
      <div style={{ padding: 8, minWidth: 200 }}>
        <Select
          value={params.localTeam}
          onChange={(teamName) => {
            params.setLocalTeam(teamName);
            if (params.onTeamChange) {
              params.onTeamChange(teamName);
            }
            popup.close();
          }}
          loading={teams.isLoading}
          placeholder="Select team..."
          style={{ width: '100%' }}
          allowClear
          showSearch
          optionFilterProp="children"
          dropdownStyle={{ zIndex: 10_000 }}
        >
          {teams.list.map((t: { name: string }) => (
            <Select.Option key={t.name} value={t.name}>
              {t.name}
            </Select.Option>
          ))}
        </Select>
      </div>
    );
  }, [params.onTeamChange, params.localTeam, params.teamPillRef, params.setLocalTeam, teams, popup]);

  return { handleFilterPillClick, handleDatePillClick, handleTeamPillClick };
}

// Utility Functions
/**
 * Converts ExploreCard data to SearchState format
 */
function convertExploreCardData(card: ExploreCard, orgId: string): SearchState {
  return {
    ...card.state.params,
    orgId,
  };
}

/**
 * Merges local filters with team filter, removing duplicates
 */
function mergeFiltersWithTeam(localFilters: Condition[], teamFilter: Condition[]): Condition[] {
  const filteredLocalFilters = (localFilters || []).filter(
    filter => filter.key !== "Activity.Team"
  );
  return [...filteredLocalFilters, ...teamFilter];
}

/**
 * Creates team filter condition
 */
function createTeamFilter(teamName: string): Condition[] {
  return [{
    key: "Activity.Team",
    target: "Activity",
    attribute: "Team",
    comparison: { IN: [teamName] },
  }] as Condition[];
}

